<script setup lang="ts">
  import {
    computed,
    inject,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
    watchEffect,
    WritableComputedRef
  } from 'vue'
  import { pendingRefocusKey } from '@/shared/models/pendingRefocus'
  import { Form, Input, InputNumber, Select, SelectOption, Checkbox, Button } from 'ant-design-vue'
  import { useWindowSize } from '@/composables/useWindowSize'
  import { AdvancedAnalysis } from '../models/AdvancedAnalysis'
  import { AdvancedAnalysisSchema } from '../models/AdvancedAnalysisSchema'
  import { AdvancedAnalysisSchemaUiFieldGroup } from '../models/AdvancedAnalysisSchemaUi'
  import { debounceTime, groupBy, map, mergeMap, Subject } from 'rxjs'
  import { parseSmartValue, replaceLast, DEFAULT_INPUT_DEBOUNCE_MS } from '@/utils'
  import { AdvancedAnalysisService } from '../services/AdvancedAnalysisService'
  import { AdvancedAnalysisField } from '../models/AdvancedAnalysisField'
  import { AdvancedAnalysisFieldType } from '../models/AdvancedAnalysisFieldType'
  import { AdvancedAnalysisUiService } from '../services/AdvancedAnalysisUiService'
  import { AppstoreOutlined, BorderOutlined } from '@ant-design/icons-vue'
  import AdvancedAnalysisHelpPopover from '../help/components/AdvancedAnalysisHelpPopover.vue'
  import SelectOrCreateAdvancedAnalysisConfiguration from './SelectOrCreateAdvancedAnalysisConfiguration.vue'
  import { AdvancedAnalysisOption } from '../models/AdvancedAnalysisOption'
  import ArrayValuesAdvancedAnalysisConfiguration from './ArrayValuesAdvancedAnalysisConfiguration.vue'
  import JsonAdvancedAnalysisConfiguration from './JsonAdvancedAnalysisConfiguration.vue'
  import { AdvancedAnalysisJsonConfigurationService } from '../services/AdvancedAnalysisJsonConfigurationService'
  import ArrayAdvancedAnalysisBuilder from './ArrayAdvancedAnalysisBuilder.vue'
  import ObjectAdvancedAnalysisBuilder from './ObjectAdvancedAnalysisBuilder.vue'
  import JsonEditorSchemaConfiguration from '@/shared/JsonEditorSchema/JsonEditorSchemaConfiguration.vue'
  import { JsonEditorSchemaType } from '@/shared/JsonEditorSchema/models/JsonEditorCdnSchemaType'

  interface IAdvancedAnalysisConfigurationProps {
    advancedAnalysisSchemas: Array<AdvancedAnalysisSchema>
    advancedAnalysis: AdvancedAnalysis | null
    rootAdvancedAnalysis: AdvancedAnalysis | null
    uiFieldGroup?: AdvancedAnalysisSchemaUiFieldGroup
    configurationLevel: number
    showDescription?: boolean
    isCondensedLayout: boolean
    globalCollapsed?: { collapsed: boolean }
    currentPath?: string[]
    isProcessingAdvancedAnalysis?: boolean
  }

  interface IAdvancedAnalysisConfigurationEmits {
    (
      e: 'configuration-updated',
      config: AdvancedAnalysis,
      newConfig: Record<string, any>,
      typeChange?: boolean
    ): void
  }

  const props = defineProps<IAdvancedAnalysisConfigurationProps>()
  const emit = defineEmits<IAdvancedAnalysisConfigurationEmits>()

  //TODO: add validation should we use forms?
  const formRef = ref()
  const configuration = ref<Record<string, any>>({})
  const isCondensedLayout = ref<boolean>(props.isCondensedLayout)
  const isCollapsed = ref<boolean>(false)
  const globalCollapsed = ref<{ collapsed: boolean }>(props.globalCollapsed ?? { collapsed: false })
  const { controlSize } = useWindowSize()

  watchEffect(() => {
    const newVal = props.globalCollapsed
    if (newVal) {
      globalCollapsed.value = newVal
      isCollapsed.value = !!newVal.collapsed
    }
  })

  const configDescription = ref(props.advancedAnalysis?.configuration?.description ?? '')

  watch(configDescription, (newVal) => {
    if (configuration.value) {
      configuration.value.description = newVal
      handleConfigurationChange()
    }
  })

  const inputValueChanged$ = new Subject<{ field: string | Array<string>; value: string }>()

  const inputValueChangedSubscription = inputValueChanged$
    .pipe(
      groupBy(({ field }) => field),
      mergeMap((group$) =>
        group$.pipe(
          debounceTime(DEFAULT_INPUT_DEBOUNCE_MS),
          map(({ field, value }) => ({ field, value }))
        )
      )
    )
    .subscribe(({ field, value }) => {
      const parsedValue = typeof value === 'string' ? parseSmartValue(value) : value
      if (Array.isArray(field)) setValueAtPath(field, parsedValue)
      else configuration.value[field] = parsedValue
      handleConfigurationChange()
    })

  // Initialize configuration with default values
  const initializeConfiguration = () => {
    if (!props.advancedAnalysis) return
    configuration.value = AdvancedAnalysisService.initializeAdvancedAnalysisConfiguration(
      props.advancedAnalysis,
      (props.advancedAnalysis.schema as any)?.fields ?? []
    )
  }

  const handleConfigurationChange = () => {
    if (props.advancedAnalysis) {
      emit('configuration-updated', props.advancedAnalysis, configuration.value)
    }
  }

  const handleChildConfigTypeChanged = (
    configType: string,
    childConfig: AdvancedAnalysis,
    configObj: any
  ) => {
    if (childConfig) {
      emit('configuration-updated', { ...childConfig, _type: configType } as any, configObj, true)
    }
  }

  const handleConfigTypeChanged = (configType: string, configObj: any) => {
    if (props.advancedAnalysis) {
      emit(
        'configuration-updated',
        { ...props.advancedAnalysis, _type: configType } as any,
        configObj?.value,
        true
      )
    }
  }

  watch(
    () => props.advancedAnalysis,
    (newConfig) => {
      if (newConfig) {
        configuration.value = { ...(newConfig.configuration ?? {}) }
        // initialize with defaults
        if (Object.keys(configuration.value).length === 0) {
          initializeConfiguration()
          // Ensure JSON Export has initial JSON config state
          handleConfigurationChange()
        }
        // ensure any non-schema properties are initialized
        configDescription.value = configuration.value.description ?? ''
      }
    },
    { immediate: true, deep: true }
  )

  const pendingRefocus = inject(pendingRefocusKey)

  let isUnmounting = false

  const onInputChange = (newValue: any, fieldName: string | Array<string>) => {
    const fieldKey = Array.isArray(fieldName) ? fieldName.join('.') : String(fieldName)
    const activeEl = document.activeElement as HTMLInputElement | null
    if (pendingRefocus) {
      pendingRefocus.value = {
        levelFieldKey: `${props.configurationLevel}:${fieldKey}`,
        cursor: activeEl?.selectionStart ?? null
      }
    }
    inputValueChanged$.next({ value: newValue, field: fieldName })
  }

  const onInputBlur = (fieldKey: string) => {
    if (isUnmounting) return
    if (pendingRefocus?.value?.levelFieldKey === `${props.configurationLevel}:${fieldKey}`) {
      pendingRefocus.value = null
    }
  }

  const toggleCollapse = () => {
    isCollapsed.value = !isCollapsed.value
  }

  const toggleGlobalCollapse = () => {
    globalCollapsed.value = { collapsed: !isCollapsed.value }
    isCollapsed.value = !isCollapsed.value
  }

  const onLayoutModeChanged = (e: any) => {
    isCondensedLayout.value = e.target.value
  }

  // use configuredFields (resolved field + path) when available, else schema.fields
  const simpleFieldCount = computed(() => {
    const fieldList: AdvancedAnalysisField[] =
      Array.isArray(configuredUiFields?.value) && configuredUiFields.value.length
        ? configuredUiFields.value.map((e: any) => e.field)
        : props.advancedAnalysis
          ? props.advancedAnalysis.schema.fields
          : []
    return fieldList.filter(
      (f: AdvancedAnalysisField) => f.field_type === AdvancedAnalysisFieldType.Text
    ).length
  })

  const pathRefs: Record<string, any> = reactive({})
  const valueMappedFieldRefWatchers = new Map<string, (() => void) | undefined>()

  const resolveFieldPath = (allFields: AdvancedAnalysisField[], segments: string[]) => {
    if (!segments?.length) return null
    let current = allFields.find((f) => f.name === segments[0])
    if (current == null) return null
    const path = [current.name]
    for (let i = 1; i < segments.length; i++) {
      const seg = segments[i]
      if (current && Array.isArray(current.fields)) {
        const child: AdvancedAnalysisField | undefined = current.fields.find(
          (f: AdvancedAnalysisField) => f.name === seg
        )
        if (child == null) return null
        current = child
        path.push(seg)
        continue
      }
      if (current && current.item_schema && typeof (current as any).item_schema === 'object') {
        const item: AdvancedAnalysisField = current.item_schema
        if (Array.isArray(item.fields)) {
          const child = item.fields.find((f: AdvancedAnalysisField) => f.name === seg)
          if (!child) return null
          current = child
          path.push(seg)
          continue
        }
        return null
      }
      return null
    }
    return { field: current as AdvancedAnalysisField, path, pathKey: path.join('.') }
  }

  const configuredUiFields = computed(() => {
    const cfg = props.advancedAnalysis
    const uiGroup = props.uiFieldGroup
    if (!cfg) return []
    const allFields = (cfg.schema as any).fields as AdvancedAnalysisField[]

    if (!uiGroup || !Array.isArray(uiGroup.fields) || uiGroup.fields.length === 0) {
      return allFields.map((f) => ({ field: f, path: [f.name], pathKey: f.name }))
    }

    const resolved: Array<{ field: AdvancedAnalysisField; path: string[]; pathKey: string }> = []

    for (const entry of uiGroup.fields) {
      if (typeof entry !== 'string') continue
      const segments = entry
        .split('.')
        .map((s) => s.trim())
        .filter(Boolean)
      const r = resolveFieldPath(allFields, segments)
      if (r) {
        resolved.push(r)
      } else {
        // policy: either warn or throw. Prefer throwing earlier via AnalysisConfigsUiService validation.
        console.warn(`uiFieldGroup entry not found in schema: ${entry}`)
      }
    }
    return resolved
  })

  const levelBasedBackgroundColor = computed(() => {
    // backgroundColorClamp configurationLevel to min 1
    const level = Math.max(props.configurationLevel, 1)
    // Opacity increases by 1% per level, starting from 0% at level 1
    const opacity = Math.min((level - 1) * 0.005, 1)
    return `rgba(0, 0, 0, ${opacity.toFixed(2)})`
  })

  const shouldShowGlobalCollapseButton = computed(() => {
    return (
      (props.advancedAnalysis?.schema as any)?.fields?.findIndex(
        (x: AdvancedAnalysisField) =>
          x.field_type === AdvancedAnalysisFieldType.Object ||
          x.field_type === AdvancedAnalysisFieldType.Json
      ) !== -1
    )
  })

  const shouldShowFieldInformation = (field: AdvancedAnalysisField): boolean => {
    return (
      field.field_type !== AdvancedAnalysisFieldType.Object &&
      field.field_type !== AdvancedAnalysisFieldType.Json
    )
  }

  //TODO move this to UIservice? Use in objectbuilder?
  const setValueAtPath = (path: string[], value: any) => {
    if (!path || path.length === 0) return
    let cur: any = configuration.value
    for (let i = 0; i < path.length - 1; i++) {
      const seg = path[i]
      if (cur[seg] == null || typeof cur[seg] !== 'object') cur[seg] = {}
      cur = cur[seg]
    }
    cur[path[path.length - 1]] = value
  }

  // create a computed ref for a nested path that reads/writes into the single canonical configuration object
  const makePathRef = (path: string[]): WritableComputedRef<any> => {
    return computed({
      get: () => {
        return AdvancedAnalysisUiService.getValueByPath(configuration.value, path)
      },
      set: (v: any) => {
        setValueAtPath(path, v)
        // reuse existing update flow so parent components stay in sync
        handleConfigurationChange()
      }
    })
  }

  function ensurePathRef(
    path: string[],
    pathKey: string,
    field: AdvancedAnalysisField,
    defaultValue?: any
  ) {
    if (!pathKey) pathKey = path.join('.')
    if (!pathRefs[pathKey]) {
      let pathRef = null
      pathRef = makePathRef(path)

      const existingValue = AdvancedAnalysisUiService.getValueByPath(configuration.value, path)
      const defaultValueWasPassed = arguments.length >= 4
      if (existingValue === undefined) {
        if (defaultValueWasPassed) pathRef.value = defaultValue
        else if (field.default_value != undefined) pathRef.value = field.default_value
      }

      pathRefs[pathKey] = pathRef
    }
    return pathRefs[pathKey]
  }

  // Used to Value Mapped fields to ensure values are synchronized
  const setupValueMappedWatcher = (
    srcSegments: string[],
    targetPath: string[],
    field: AdvancedAnalysisField,
    preserveExisting?: boolean,
    srcValueConditions?: Array<{ value_prop_path: string; src_prop_value: any }>
  ) => {
    const srcKey = srcSegments.join('.')
    const watchKey = `${srcKey}=>${targetPath.join('.')}`

    if (valueMappedFieldRefWatchers.has(watchKey)) return

    // ensure a reactive ref exists for the source path (raw)
    if (!pathRefs[srcKey]) {
      pathRefs[srcKey] = makePathRef(srcSegments)
    }

    // safe getter that prefers the reactive pathRef when available,
    // otherwise falls back to reading the canonical configuration object.
    const getSrcValue = () => {
      let value: any = undefined

      const maybeRef = pathRefs[srcKey]
      if (maybeRef && typeof maybeRef === 'object' && 'value' in maybeRef) {
        value = maybeRef.value
      }
      // fallback: read directly from configuration (defensive)
      value = AdvancedAnalysisUiService.getValueByPath(configuration.value, srcSegments)

      if (Array.isArray(srcValueConditions)) {
        const match = srcValueConditions.find((x) => x.src_prop_value === value)
        if (match)
          value = AdvancedAnalysisUiService.getValueByPath(
            configuration.value,
            match.value_prop_path
          )
      }

      return value
    }

    // initial sync: transform source value into target form and write to target path
    try {
      const oldValue = preserveExisting
        ? AdvancedAnalysisUiService.getValueByPath(configuration.value, targetPath)
        : undefined
      const transformed = AdvancedAnalysisUiService.transformValueForValueMappedField(
        field,
        getSrcValue(),
        oldValue
      )
      setValueAtPath(targetPath, transformed)
      handleConfigurationChange()
    } catch (err) {
      console.warn('Failed initial sync for mapped field', watchKey, err)
    }

    // watch source via safe getter and copy changes to target
    const stop = watch(getSrcValue, (newVal) => {
      try {
        const oldValue = preserveExisting
          ? AdvancedAnalysisUiService.getValueByPath(configuration.value, targetPath)
          : undefined
        const transformed = AdvancedAnalysisUiService.transformValueForValueMappedField(
          field,
          newVal,
          oldValue
        )
        setValueAtPath(targetPath, transformed)
        handleConfigurationChange()
      } catch (err) {
        console.warn('Failed mapped-field watch handler', watchKey, err)
      }
    })

    valueMappedFieldRefWatchers.set(watchKey, stop)
  }

  // When configuredUiFields changes, ensure refs exist
  watch(
    () => configuredUiFields.value.map((f: any) => f.pathKey),
    (keys) => {
      if (!Array.isArray(keys)) return
      keys.forEach((k: string) => {
        const entry = configuredUiFields.value.find((e: any) => e.pathKey === k)
        if (entry) {
          ensurePathRef(entry.path, entry.pathKey, entry.field)

          // field_type_args mappings
          // Value Mapped handled in ensurePathRef
          if (AdvancedAnalysisUiService.isValueMappedField(entry.field)) {
            const srcPropPath = entry.field.field_type_args?.value_map?.src_prop_path
            const preserveExisting = !!entry.field.field_type_args?.value_map?.preserve_existing
            const srcSegments = srcPropPath
              .split('.')
              .map((s: string) => s.trim())
              .filter(Boolean)
            const srcValueConditions = entry.field.field_type_args?.value_map?.src_value_conditions
            // create a raw ref and setup watcher to sync source->target
            setupValueMappedWatcher(
              srcSegments,
              entry.path,
              entry.field,
              preserveExisting,
              srcValueConditions
            )
          }

          // XOR - ensure a pathRef exists for an alternate field name (used by SelectXorText)
          const altName = entry.field.field_type_args?.xor_alt_field_name
          if (typeof altName === 'string' && altName.length > 0) {
            const defaultValue = entry.field.field_type_args?.xor_inactive_field_default_value
            const altPath = replaceLast(entry.path, altName)
            const altKey = altPath.join('.')
            if (!pathRefs[altKey]) {
              // pass the same field object — ensurePathRef doesn't rely strictly on this being
              // the exact schema field for the alternate path (it only uses default_value if present)
              ensurePathRef(altPath, altKey, entry.field, defaultValue)
            }
          }

          //end field_type_args mappings
        }
      })
    },
    { immediate: true }
  )

  const checkboxSizeClass = computed(() => {
    if (controlSize.value === 'small') return 'checkbox-sm'
    if (controlSize.value === 'large') return 'checkbox-lg'
    return 'checkbox-md'
  })

  const displayField = computed(() => {
    const mappedField = props.advancedAnalysis?.display_field as
      | string
      | Array<string>
      | null
      | undefined
    if (mappedField == null) return undefined

    const fields = props.advancedAnalysis?.schema?.fields ?? []

    const valueForName = (name: string) => {
      const matchingField = fields.find((x: any) => x.name === name)
      if (!matchingField) return undefined
      const val = configuration.value?.[matchingField.name]
      return val === undefined ? undefined : val
    }

    if (typeof mappedField === 'string') {
      return valueForName(mappedField)
    }

    if (Array.isArray(mappedField)) {
      for (const name of mappedField) {
        const v = valueForName(name)
        if (v) return v
      }
    }

    return undefined
  })

  onMounted(() => {
    if (pendingRefocus?.value) {
      const { levelFieldKey, cursor } = pendingRefocus.value
      let el = document.querySelector(`[data-field="${levelFieldKey}"]`) as HTMLElement | null
      if (el && el.tagName !== 'INPUT') el = el.querySelector('input') ?? el
      const inputEl = el as HTMLInputElement | null
      if (inputEl?.tagName === 'INPUT') {
        inputEl.focus()
        if (cursor !== null) {
          try {
            inputEl.setSelectionRange(cursor, cursor)
          } catch {}
        }
      }
      pendingRefocus.value = null
    }
  })

  onBeforeUnmount(() => {
    isUnmounting = true
    inputValueChangedSubscription?.unsubscribe()
    valueMappedFieldRefWatchers.forEach((stop) => {
      if (typeof stop === 'function') stop()
    })
    valueMappedFieldRefWatchers.clear()
  })
</script>

<template>
  <div
    v-if="advancedAnalysis"
    :style="{ backgroundColor: levelBasedBackgroundColor }"
    :class="[
      'border',
      'advanced-analysis-configuration',
      'h-full',
      'w-full',
      {
        '!bg-[var(--card)] dark:!bg-[var(--card-light)]': props.configurationLevel % 2 !== 0,
        '!bg-[var(--card-light)] dark:!bg-[var(--card)]': props.configurationLevel % 2 === 0,
        'child-config-configuration': props.configurationLevel > 1
      }
    ]"
  >
    <div class="flex h-full flex-col">
      <!-- Only show radio group if configurationLevel is 1 or less -->
      <div v-if="props.configurationLevel <= 1" class="mb-2 flex items-center justify-end gap-2">
        <a-tooltip>
          <template #title>Toggle layout mode</template>
          <a-radio-group
            :value="isCondensedLayout"
            @change="onLayoutModeChanged"
            :size="controlSize"
          >
            <a-radio-button :value="true">
              <AppstoreOutlined class="radio-icon-center" />
            </a-radio-button>
            <a-radio-button :value="false">
              <BorderOutlined class="radio-icon-center" />
            </a-radio-button>
          </a-radio-group>
        </a-tooltip>
      </div>
      <div class="horizontal-rule mb-4 border-b pb-1" v-if="props.configurationLevel > 1">
        <div class="flex">
          <div>
            <h3 class="mb-1 text-lg font-semibold">
              Configure: {{ advancedAnalysis.display_name }}
            </h3>
            <span class="ml-1" v-if="displayField != null">{{ `(${displayField})` }}</span>
          </div>
          <div class="ml-auto flex">
            <a-tooltip placement="top">
              <template #title>Toggle Collapse</template>
              <Button
                style="padding: 3px; border: none"
                type="text"
                :size="controlSize"
                class="flex items-center justify-center"
                @click="toggleCollapse"
                aria-label="Toggle Collapse"
              >
                <PlusSquare
                  v-if="isCollapsed"
                  class="text-base"
                  style="line-height: 1; margin: 0"
                />
                <MinusSquare v-else class="text-base" style="line-height: 1; margin: 0" />
              </Button>
            </a-tooltip>

            <a-tooltip v-if="shouldShowGlobalCollapseButton" placement="top">
              <template #title>{{ isCollapsed ? 'Expand All' : 'Collapse All' }}</template>
              <Button
                v-if="shouldShowGlobalCollapseButton"
                style="padding: 3px; border: none"
                type="text"
                :size="controlSize"
                class="flex items-center justify-center"
                @click="toggleGlobalCollapse"
                aria-label="Toggle Collapse All"
              >
                <ExpandOutlined
                  v-if="isCollapsed"
                  class="rotate-90 rounded-sm border-gray-300 p-1 text-base"
                  style="line-height: 1; margin: 0"
                />
                <CompressOutlined
                  v-else
                  class="rotate-90 rounded-sm border-gray-300 p-1 text-base"
                  style="line-height: 1; margin: 0"
                />
              </Button>
            </a-tooltip>
          </div>
        </div>
      </div>
      <div
        v-else-if="
          advancedAnalysis._type !== AdvancedAnalysisService.CUSTOM_ADVANCED_ANALYSIS_TYPE &&
          props.showDescription === true
        "
        class="mb-4"
      >
        <label class="mb-1 block text-sm font-medium">Name</label>
        <Input
          v-model:value="configDescription"
          @change="handleConfigurationChange"
          placeholder="Configuration Name"
          :size="controlSize"
        />
      </div>

      <!-- Configuration Form -->
      <div
        :class="['flex-1', 'overflow-y-auto', { 'collapsed-config-configuration': isCollapsed }]"
      >
        <Form ref="formRef" :model="configuration" layout="vertical">
          <div
            :class="{
              'config-form-fields-grid': true,
              expanded:
                props.configurationLevel === 1 ? !isCondensedLayout : !props.isCondensedLayout
            }"
          >
            <div
              v-for="configuredField in configuredUiFields"
              :key="configuredField.field.name"
              class="config-form-field-grid"
              :class="{
                'array-config-builder-grid-span':
                  configuredField.field.field_type === AdvancedAnalysisFieldType.Array,
                'object-config-builder-grid-span':
                  configuredField.field.field_type === AdvancedAnalysisFieldType.Object,
                'json-config-configuration-grid-span':
                  configuredField.field.field_type === AdvancedAnalysisFieldType.Json,
                'single-field-grid-span':
                  simpleFieldCount <= 1 &&
                  configuredField.field.field_type !== AdvancedAnalysisFieldType.Object &&
                  configuredField.field.field_type !== AdvancedAnalysisFieldType.Array &&
                  configuredField.field.field_type !== AdvancedAnalysisFieldType.Json,
                '!hidden': !AdvancedAnalysisUiService.isFieldVisibleFromRoot(
                  configuredField.field,
                  props.rootAdvancedAnalysis,
                  props.currentPath
                    ? [...props.currentPath, ...configuredField.path]
                    : configuredField.path
                )
              }"
            >
              <div class="flex">
                <div
                  v-if="shouldShowFieldInformation(configuredField.field)"
                  class="min-w-0 flex-1"
                >
                  <label class="table-field-label mb-1 block text-sm font-medium">
                    {{ configuredField.field.display_name }}
                    <span v-if="configuredField.field.required" class="text-red-500">*</span>
                  </label>
                  <p
                    v-if="configuredField.field.description"
                    class="table-field-label mb-2 text-xs"
                  >
                    {{ configuredField.field.description }}
                  </p>
                </div>

                <div
                  v-if="
                    configuredField.field.help_content != null &&
                    configuredField.field.help_content?.length > 0
                  "
                  class="m1-auto flex justify-end"
                >
                  <AdvancedAnalysisHelpPopover
                    :content-type="configuredField.field.help_content"
                    :title="`${configuredField.field.display_name} Help`"
                  />
                </div>
              </div>

              <!-- Text Input -->
              <Input
                v-if="configuredField.field.field_type === AdvancedAnalysisFieldType.Text"
                :value="pathRefs[configuredField.pathKey]"
                @update:value="(val: string) => onInputChange(val, configuredField.path)"
                @blur="() => onInputBlur(configuredField.pathKey)"
                :data-field="`${props.configurationLevel}:${configuredField.pathKey}`"
                :placeholder="configuredField.field.placeholder || ''"
                :readonly="
                  configuredField.field.readonly ||
                  !!configuredField.field.field_type_args?.value_map?.src_prop_path
                "
                :disabled="
                  configuredField.field.readonly ||
                  !!configuredField.field.field_type_args?.value_map?.src_prop_path
                "
                :size="controlSize"
              />

              <!-- Number Input -->
              <InputNumber
                v-else-if="configuredField.field.field_type === AdvancedAnalysisFieldType.Number"
                :value="pathRefs[configuredField.pathKey]"
                @update:value="
                  (val: number) => {
                    setValueAtPath(configuredField.path, val)
                    handleConfigurationChange()
                  }
                "
                :placeholder="configuredField.field.placeholder || ''"
                :size="controlSize"
                :disabled="configuredField.field.readonly"
                :readonly="configuredField.field.readonly"
                class="w-full"
              />

              <!-- Select Dropdown -->
              <Select
                v-else-if="configuredField.field.field_type === AdvancedAnalysisFieldType.Select"
                v-model:value="pathRefs[configuredField.pathKey]"
                :placeholder="`Select ${configuredField.field.display_name.toLowerCase()}`"
                @change="handleConfigurationChange"
                :size="controlSize"
                :disabled="configuredField.field.readonly"
                :readonly="configuredField.field.readonly"
                class="w-full"
                showSearch
                allowClear
              >
                <SelectOption
                  v-for="option in configuredField.field.options"
                  :key="option.value"
                  :value="option.value"
                >
                  <span v-html="option.label"></span>
                </SelectOption>
              </Select>

              <!-- Select Dropdown Or Create -->
              <SelectOrCreateAdvancedAnalysisConfiguration
                v-else-if="
                  configuredField.field.field_type === AdvancedAnalysisFieldType.SelectOrCreate
                "
                v-model="pathRefs[configuredField.pathKey]"
                :placeholder="`Select or enter new ${configuredField.field.display_name.toLowerCase()}`"
                :options="configuredField.field.options as AdvancedAnalysisOption[]"
                :disabled="configuredField.field.readonly"
                :readonly="configuredField.field.readonly"
                @update:modelValue="handleConfigurationChange"
              />

              <!-- Multi-Select -->
              <Select
                v-else-if="
                  configuredField.field.field_type === AdvancedAnalysisFieldType.MultiSelect
                "
                v-model:value="pathRefs[configuredField.pathKey]"
                mode="multiple"
                :placeholder="`Select ${configuredField.field.display_name.toLowerCase()}`"
                @change="handleConfigurationChange"
                :size="controlSize"
                :disabled="configuredField.field.readonly"
                :readonly="configuredField.field.readonly"
                class="w-full"
                showSearch
                allowClear
              >
                <SelectOption
                  v-if="configuredField.field.field_type === AdvancedAnalysisFieldType.MultiSelect"
                  v-for="option in configuredField.field.options"
                  :key="option.value"
                  :value="option.value"
                >
                  <span v-html="option.label"></span>
                </SelectOption>
              </Select>

              <!-- Boolean Checkbox -->
              <Checkbox
                v-else-if="configuredField.field.field_type === AdvancedAnalysisFieldType.Boolean"
                @change="handleConfigurationChange"
                v-model:checked="pathRefs[configuredField.pathKey]"
                :disabled="configuredField.field.readonly"
                :readonly="configuredField.field.readonly"
                :class="checkboxSizeClass"
              >
                {{ configuredField.field.display_name }}
              </Checkbox>

              <!-- Array Values -->
              <ArrayValuesAdvancedAnalysisConfiguration
                v-else-if="
                  configuredField.field.field_type === AdvancedAnalysisFieldType.ArrayValues
                "
                v-model="pathRefs[configuredField.pathKey]"
                :place-holder="configuredField.field.placeholder || 'Enter value(s)'"
                @update:modelValue="handleConfigurationChange"
              />

              <!-- Array Type -->
              <ArrayAdvancedAnalysisBuilder
                v-else-if="configuredField.field.field_type === AdvancedAnalysisFieldType.Array"
                :root-advanced-analysis="props.rootAdvancedAnalysis"
                :configuration-level="props.configurationLevel"
                v-model="pathRefs[configuredField.pathKey]"
                :advanced-analysis-field="configuredField.field"
                :key="advancedAnalysis?.id"
                @child-config-type-changed="handleChildConfigTypeChanged"
                :config-schemas="advancedAnalysisSchemas"
                :is-condensed-layout="
                  props.configurationLevel === 1 ? isCondensedLayout : props.isCondensedLayout
                "
                :global-collapsed="globalCollapsed"
                :current-path="
                  props.currentPath
                    ? [...props.currentPath, ...configuredField.path]
                    : configuredField.path
                "
              />

              <!-- Object Type -->
              <ObjectAdvancedAnalysisBuilder
                v-else-if="configuredField.field.field_type === AdvancedAnalysisFieldType.Object"
                :root-advanced-analysis="props.rootAdvancedAnalysis"
                :configuration-level="props.configurationLevel + 1"
                v-model="pathRefs[configuredField.pathKey]"
                :advanced-analysis-field="configuredField.field"
                @update:modelValue="handleConfigurationChange"
                :config-schemas="advancedAnalysisSchemas"
                :is-condensed-layout="
                  props.configurationLevel === 1 ? isCondensedLayout : props.isCondensedLayout
                "
                :global-collapsed="globalCollapsed"
                :current-path="
                  props.currentPath
                    ? [...props.currentPath, ...configuredField.path]
                    : configuredField.path
                "
              />

              <!-- CDN SChema JSON Editors -->
              <JsonEditorSchemaConfiguration
                v-else-if="
                  configuredField.field.field_type === JsonEditorSchemaType.RuleSchema ||
                  configuredField.field.field_type === JsonEditorSchemaType.AnalysisConfigSchema ||
                  configuredField.field.field_type === JsonEditorSchemaType.CalculationSchema ||
                  configuredField.field.field_type === JsonEditorSchemaType.PlotSchema ||
                  configuredField.field.field_type === JsonEditorSchemaType.TableSchema ||
                  configuredField.field.field_type === JsonEditorSchemaType.CustomReportSchema
                "
                :schema-type="configuredField.field.field_type"
                v-model="pathRefs[configuredField.pathKey]"
                :configuration-level="props.configurationLevel"
                :is-processing-entity="isProcessingAdvancedAnalysis"
                @update:modelValue="handleConfigurationChange"
              />

              <!-- Json Type (Custom Config) -->
              <JsonAdvancedAnalysisConfiguration
                v-else-if="configuredField.field.field_type === AdvancedAnalysisFieldType.Json"
                :advanced-analysis-schemas="props.advancedAnalysisSchemas"
                v-model="configuration"
                :editor-schema="
                  AdvancedAnalysisJsonConfigurationService.getJsonEditorSchemaForAdvancedAnalysis()
                "
                :place-holder="configuredField.field.placeholder || 'Configure Json'"
                :configuration-level="props.configurationLevel"
                @advanced-analysis-type-changed="handleConfigTypeChanged"
                @update:modelValue="handleConfigurationChange"
                :allow-type-change="true"
              />

              <!-- Default fallback for unknown field types -->
              <JsonAdvancedAnalysisConfiguration
                v-else
                :advanced-analysis-schemas="props.advancedAnalysisSchemas"
                v-model="pathRefs[configuredField.pathKey]"
                :editor-schema="
                  AdvancedAnalysisJsonConfigurationService.getJsonEditorSchemaForField(
                    configuredField.field
                  )
                "
                :place-holder="configuredField.field.placeholder || 'Configure Json'"
                :configuration-level="props.configurationLevel"
                @update:modelValue="handleConfigurationChange"
                :allow-type-change="false"
              />
            </div>
          </div>
        </Form>
      </div>
    </div>
  </div>

  <!-- Empty State -->
  <div v-else class="flex h-full items-center justify-center text-gray-500">
    <div class="mt-8 text-center">
      <p class="mb-2 text-lg">No Advanced Analysis Selected</p>
      <p class="text-sm">
        Select an advanced analysis from the left panel to configure its settings
      </p>
    </div>
  </div>
</template>

<style scoped>
  .advanced-analysis-configuration {
    padding: 10px;
  }

  .collapsed-config-configuration {
    display: none;
  }

  .horizontal-rule {
    border-bottom-color: #0000001f;
  }

  /* Custom scrollbar for form area */
  .overflow-y-auto::-webkit-scrollbar {
    width: 6px;
  }

  .overflow-y-auto::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  .overflow-y-auto::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }

  .overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }

  .config-form-fields-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
  }

  @media (min-width: 1100px) {
    .config-form-fields-grid:not(.expanded) {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  .config-form-field-grid {
    width: 100%;
    min-height: 90px;
    max-height: 130px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    overflow: hidden;
  }

  .array-config-builder-grid-span,
  .object-config-builder-grid-span,
  .json-config-configuration-grid-span,
  .single-field-grid-span {
    grid-column: 1 / -1 !important;
    min-height: unset;
    max-height: unset;
    overflow: visible;
  }

  .plot-field-label,
  .table-field-label {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    display: block;
  }

  .plot-field-description {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    display: block;
  }

  .radio-icon-center {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    font-size: 18px;
  }

  /* Ensure checkbox size classes are available (RuleConfiguration already added these; keep consistent) */
  .checkbox-sm {
    font-size: 12px;
  }
  .checkbox-sm .ant-checkbox-inner {
    width: 14px;
    height: 14px;
  }
  .checkbox-md {
    font-size: 14px;
  }
  .checkbox-md .ant-checkbox-inner {
    width: 16px;
    height: 16px;
  }
  .checkbox-lg {
    font-size: 16px;
  }
  .checkbox-lg .ant-checkbox-inner {
    width: 20px;
    height: 20px;
  }
</style>
